// Taşeron portalı — İzin detayı: Araçlar (api-contract §23.2/§23.4). İzin aracı KOPYALAMAZ, BAĞLAR:
// "Araç ekle" firmanın araç kaydından seçtirir (+ yeni plaka), "İzinden çıkar" yalnız bağı kaldırır.
// Kartlar emekli: araç bir REFERANS TABLOSUDUR (Plaka | Belgeler n/m | Hazırlık | ⋮); belgeler aracın
// kendisindedir ve plaka düğmesinin açtığı ortak `VehicleSheet`'te (izin bağlamı) yönetilir. Plakayı
// bu ekrandan değiştirmek YOK (düzenleme Araçlar sayfasında). Sheet/yükleme hedefi satır DEĞİL uuid
// saklar: her render'da `vehicles` prop'undan bulunur, aksi hâlde belge listesi/hazırlık yüklemeden
// sonra tazelenmez. Odak desenleri `pages/VehiclesPage.tsx`'in AYNISI. Onay/red İSG uzmanındadır (§23.1).

import {
  ConfirmDialog,
  EmptyState,
  PermitVehicleAddSheet,
  RowActionsMenu,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableToolbar,
  Tag,
  VehicleSheet,
  type WorkPermitVehicleDto,
  cn,
  countReadyVehicles,
  formatProgress,
  supplierPermitApi,
  useAuthStore,
  useRowRemovalFocus,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Truck } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invalidateVehicleCaches } from '../../lib/vehicleCache';
import { useVehicleClient } from '../../lib/vehicleClient';
import { vehicleReadyColor } from '../status/tone';
import { VehicleDocumentUploadModal } from '../vehicles/VehicleDocumentUploadModal';
import { VehicleDocuments } from '../vehicles/VehicleDocuments';
import { RequiredDocumentsList } from './RequiredDocumentsList';

interface PermitVehiclesPanelProps {
  permitUuid: string;
  vehicles: WorkPermitVehicleDto[];
  /** İznin tipinde istenen araç belge türleri — `vehicle` entity'sinin §27 kayıt kodları (§22/§23). */
  requiredVehicleDocuments: string[];
  /**
   * `vehicles`'ı taşıyan izin detayı sonucunun zaman damgaları — AYNI sorgu sonucundan gelir.
   * Panel kendi gözlemcisiyle okusaydı, panelin tek başına render'ında (ör. kayıt listesi yenilendi)
   * önbellekteki yeni damga eski `vehicles` prop'uyla buluşur ("yenileme bitti, satır duruyor") ve
   * bekleyen odak talebini satır kalkmadan düşürürdü.
   */
  source: { dataUpdatedAt: number; errorUpdatedAt: number };
}

export function PermitVehiclesPanel({
  permitUuid,
  vehicles,
  requiredVehicleDocuments,
  source,
}: PermitVehiclesPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const companyId = useAuthStore((s) => s.principal?.companyId ?? null);
  const vehicleClient = useVehicleClient();
  const [addOpen, setAddOpen] = useState(false);
  const [sheetUuid, setSheetUuid] = useState<string | null>(null);
  const [uploadUuid, setUploadUuid] = useState<string | null>(null);
  // Yükleme penceresi oturumu: hedef her null→uuid (ya da uuid→başka uuid) geçişinde artar;
  // modal `key` olarak alır → yeni açılış taze durumla başlar, kapanış AYNI örnekte kalır
  // (kapanış animasyonu, odak dönüşü, uçuştaki yüklemenin onSuccess/onError'ı korunur).
  const [uploadPrev, setUploadPrev] = useState<string | null>(null);
  const [uploadSession, setUploadSession] = useState(0);
  if (uploadUuid !== uploadPrev) {
    setUploadPrev(uploadUuid);
    if (uploadUuid !== null) {
      setUploadSession((n) => n + 1);
    }
  }
  const [toUnlink, setToUnlink] = useState<WorkPermitVehicleDto | null>(null);
  const addButtonRef = useRef<HTMLButtonElement | null>(null);
  const plateOpenerRef = useRef<HTMLElement | null>(null);
  const plateButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const rowKeys = useMemo(() => vehicles.map((v) => v.uuid), [vehicles]);

  // Kayıt listesini ısıtır: ekleme çekmecesi AÇILDIĞINDA hazır olsun — anahtar ve sorgu çekmecenin
  // kendisiyle AYNI istemciden (`vehicleClient`), yani aynı önbellek girdisi.
  useQuery({
    queryKey: vehicleClient.registryKey,
    queryFn: vehicleClient.listRegistry,
    enabled: vehicleClient.registryEnabled,
    staleTime: 60_000,
  });
  const readyCount = countReadyVehicles(vehicles);
  const sheetVehicle = vehicles.find((v) => v.uuid === sheetUuid) ?? null;
  const uploadVehicle = vehicles.find((v) => v.uuid === uploadUuid) ?? null;

  // Hedef araç listeden kalkınca saklanan uuid bayatlamasın: odak efekti örtü açık sanmasın ve
  // sonradan yeniden bağlanan/eklenen aynı araç eski durumla kendiliğinden açılmasın.
  useEffect(() => {
    if (sheetUuid !== null && sheetVehicle === null) {
      setSheetUuid(null);
    }
    if (uploadUuid !== null && uploadVehicle === null) {
      setUploadUuid(null);
    }
  }, [sheetUuid, sheetVehicle, uploadUuid, uploadVehicle]);

  // Satır menüsünden açılan diyaloglar (yükle / çıkar) odağı o satırın plaka düğmesine döndürür;
  // çıkarılan satır listeden kalkınca ve diyalog kapanınca odak "Araç ekle"ye iner (ortak durum
  // makinesi). Damgalar `source`'tan: `vehicles` ile AYNI sorgu sonucu (prop notu).
  const focus = useRowRemovalFocus({
    rowKeys,
    stamps: source,
    addButtonRef,
    dialogOpen: toUnlink !== null,
    otherSurfaceOpen: sheetVehicle !== null || uploadVehicle !== null || addOpen,
  });

  const unlinkMutation = useMutation({
    mutationFn: (vehicleUuid: string) =>
      supplierPermitApi.removeSupplierPermitVehicle(permitUuid, vehicleUuid),
    onSuccess: (_data, vehicleUuid) => {
      invalidateVehicleCaches(queryClient, companyId);
      // Bağ sunucuda kalktı → satır yenilemede gidecek; diyaloğun kapanış dönüşü artık o satırın
      // plaka düğmesine DEĞİL, kalıcı "Araç ekle"ye iner (yenileme ne zaman gelirse gelsin).
      focus.removed(vehicleUuid);
      setToUnlink(null);
    },
    // 404 VEHICLE_NOT_FOUND: bağ zaten yok (bayat liste) → tazele; satır yenilemede kalkar.
    onError: (_error, vehicleUuid) => {
      invalidateVehicleCaches(queryClient, companyId);
      focus.removeFailed(vehicleUuid);
    },
  });

  return (
    <div className="flex flex-col">
      <TableToolbar
        summary={
          vehicles.length > 0
            ? t('permits.readiness.summary', { ready: readyCount, total: vehicles.length })
            : undefined
        }
        onNew={() => setAddOpen(true)}
        newLabel={t('permits.vehicles.add')}
        newButtonRef={addButtonRef}
      />

      {/* İstenen araç belgeleri (iznin tipi — §22 `entity = vehicle`); her araç bunları karşılamalıdır. */}
      <RequiredDocumentsList
        heading={t('permits.vehicles.requiredDocuments')}
        emptyText={t('permits.vehicles.requiredDocumentsEmpty')}
        documents={requiredVehicleDocuments.map((documentType) => ({ documentType, note: null }))}
        entity="vehicle"
      />

      {/* `relative`: başlıktaki `sr-only` metin taşan tabloda kaydırıcıdan kaçıp sayfayı kaydırmasın.
          Personel sekmesiyle AYNI sütun yapısı: plaka sütunu kalan genişliği alır ve kırpılır (≥sm
          `w-full` + `max-w-0`); düğme `w-fit` → alt çizgi/odak halkası yalnız metni kaplar, boş
          hücreyi değil. <sm sabit 10rem. */}
      <Table caption={t('permits.vehicles.section')} className="relative">
        <TableHeader>
          <TableRow>
            <TableHead className="w-full truncate sm:max-w-0" title={t('vehicles.col.plate')}>
              {t('vehicles.col.plate')}
            </TableHead>
            <TableHead className="text-right">{t('vehicles.col.documents')}</TableHead>
            <TableHead>{t('permits.vehicles.col.readiness')}</TableHead>
            <TableHead className="w-12">
              <span className="sr-only">{t('common.actions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {vehicles.length === 0 ? (
            <TableMessageRow colSpan={4}>
              <EmptyState icon={Truck} title={t('permits.vehicles.empty')} />
            </TableMessageRow>
          ) : (
            vehicles.map((vehicle) => {
              const satisfiedCount =
                requiredVehicleDocuments.length - vehicle.missingVehicleDocumentTypes.length;
              return (
                <TableRow key={vehicle.uuid}>
                  <TableCell truncate={false} className="sm:max-w-0">
                    <button
                      type="button"
                      ref={(el) => {
                        if (el) {
                          plateButtonRefs.current.set(vehicle.uuid, el);
                        } else {
                          plateButtonRefs.current.delete(vehicle.uuid);
                        }
                      }}
                      onClick={(e) => {
                        plateOpenerRef.current = e.currentTarget;
                        setSheetUuid(vehicle.uuid);
                      }}
                      title={vehicle.plate}
                      className="block w-40 truncate sm:w-fit sm:max-w-full text-left font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {vehicle.plate}
                    </button>
                  </TableCell>
                  <TableCell
                    className={cn(
                      'tnum text-right',
                      requiredVehicleDocuments.length === 0 && 'text-muted',
                    )}
                  >
                    {formatProgress(satisfiedCount, requiredVehicleDocuments.length)}
                  </TableCell>
                  <TableCell>
                    <Tag color={vehicleReadyColor(vehicle.vehicleReady)}>
                      {vehicle.vehicleReady
                        ? t('permits.vehicles.ready')
                        : t('permits.vehicles.notReady')}
                    </Tag>
                  </TableCell>
                  <TableCell className="w-12 px-2">
                    <RowActionsMenu
                      ariaLabel={t('vehicles.action.openMenu', { plate: vehicle.plate })}
                      items={[
                        {
                          key: 'upload',
                          label: t('vehicles.action.upload'),
                          onSelect: () => {
                            focus.rememberOpener(plateButtonRefs.current.get(vehicle.uuid) ?? null);
                            setUploadUuid(vehicle.uuid);
                          },
                        },
                        {
                          key: 'unlink',
                          label: t('vehicles.unlink.action'),
                          destructive: true,
                          onSelect: () => {
                            unlinkMutation.reset();
                            focus.rememberOpener(plateButtonRefs.current.get(vehicle.uuid) ?? null);
                            setToUnlink(vehicle);
                          },
                        },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      <PermitVehicleAddSheet
        client={vehicleClient}
        open={addOpen}
        onOpenChange={setAddOpen}
        permitUuid={permitUuid}
        linkedUuids={rowKeys}
        restoreFocusRef={addButtonRef}
      />

      <VehicleSheet
        open={sheetVehicle !== null}
        onOpenChange={(open) => !open && setSheetUuid(null)}
        actor="contractor"
        context={sheetVehicle ? { kind: 'permit', vehicle: sheetVehicle } : null}
        restoreFocusRef={plateOpenerRef}
        documents={
          sheetVehicle ? <VehicleDocuments companyId={companyId} vehicle={sheetVehicle} /> : null
        }
        inactiveMissingNote={t('documentTypes.inactiveMissing')}
        inactiveMissingSuffix={t('documentTypes.inactiveSuffix')}
      />

      <VehicleDocumentUploadModal
        // Her yeni açılışta dosya/tür/not/hata durumu sıfırlansın (hedef kalkıp dönse bile).
        key={uploadSession}
        open={uploadVehicle !== null}
        onOpenChange={(open) => !open && setUploadUuid(null)}
        companyId={companyId}
        vehicleUuid={uploadUuid ?? ''}
        vehiclePlate={uploadVehicle?.plate ?? ''}
        restoreFocusRef={focus.menuRestoreRef}
      />

      <ConfirmDialog
        open={toUnlink !== null}
        onOpenChange={(open) => !open && setToUnlink(null)}
        title={t('vehicles.unlink.title')}
        body={t('vehicles.unlink.body', { plate: toUnlink?.plate ?? '' })}
        confirmLabel={t('vehicles.unlink.confirm')}
        restoreFocusRef={focus.menuRestoreRef}
        loading={unlinkMutation.isPending}
        error={unlinkMutation.isError ? unlinkMutation.error : undefined}
        onConfirm={() => toUnlink && unlinkMutation.mutate(toUnlink.uuid)}
      />
    </div>
  );
}
